import api from './api';

/**
 * Upload Service
 * Handles file uploads to AWS S3 via pre-signed URLs
 * This service is designed to be reusable across the platform
 */
export const uploadService = {
  /**
   * Main entry point for uploading a file to S3
   * @param {File} file - The file object from an input element
   * @param {string} folder - Optional folder name in S3 (e.g., 'agreements', 'research')
   * @returns {Promise<{url: string, key: string}>} - The public URL and S3 key of the uploaded file
   */
  async uploadFile(file, folder = 'general') {
    try {
      // Generate a unique subfolder to prevent filename collisions
      const uniqueId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      const targetFolder = `${folder}/${uniqueId}`;

      // 1. Get pre-signed URL from our backend
      const { uploadUrl, fileKey } = await this.getPresignedUrl(file.name, file.type, targetFolder);

      // 2. Upload the file directly to S3 using the pre-signed URL
      await this.uploadToS3(uploadUrl, file);

      // 3. Return the key and the clean URL (without query params)
      const publicUrl = uploadUrl.split('?')[0];
      return { url: publicUrl, key: fileKey };
    } catch (error) {
      console.error('S3 Upload Error:', error);
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  },

  /**
   * Request a pre-signed URL from the backend
   */
  async getPresignedUrl(fileName, fileType, folder) {
    const response = await api.post('/cloud/upload/s3/generate-presigned-url', {
      fileName,
      fileType,
      folder
    });
    return response.data || response;
  },

  /**
   * Request a pre-signed URL for downloading a file from S3
   */
  async getDownloadPresignedUrl(fileUrl) {
    const response = await api.post('/cloud/download/s3/generate-presigned-url-from-url', {
      fileUrl
    });
    return response.data || response;
  },

  /**
   * Download a file from S3 by generating a pre-signed URL first
   */
  async downloadFile(fileUrl) {
    try {
      const { presignedUrl } = await this.getDownloadPresignedUrl(fileUrl);
      if (presignedUrl) {
        window.open(presignedUrl, '_blank');
      } else {
        throw new Error('No download URL returned');
      }
    } catch (error) {
      console.error('S3 Download Error:', error);
      throw new Error(`Failed to download file: ${error.message}`);
    }
  },

  /**
   * Perform the actual PUT request to S3
   */
  async uploadToS3(url, file) {
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed with status ${response.status}`);
    }

    return true;
  },

  /**
   * Delete a file from S3 (via backend proxy)
   */
  async deleteFile(fileKey) {
    try {
      return await api.delete(`/cloud/delete/s3/file?key=${encodeURIComponent(fileKey)}`);
    } catch (error) {
      console.error('S3 Delete Error:', error);
      // Fail silently as this is a cleanup operation
      return false;
    }
  }
};

export default uploadService;
